import { FunctionComponent } from "react"
import styles from "./component.module.scss"
import Input from "../../../shared/input"
import { WellNotesFormT } from "./form"
import { ApolloError } from "@apollo/client"
import classNames from "classnames"
import Alert from "../../../shared/alert"
import Button from "../../../shared/button"
import { WellNoteVisibility, ZoneInfo } from "../../../../generated/graphql"
import { getDisplayStringFromZoneInfo } from "../../../../graphql/enum/zoneInfo"

interface WellNotesComponentI {
    // input change handlers
    handleNoteChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
    handleOccurredAtChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleOccurredUntilChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleVisibilityChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
    // blur handlers
    handleOccurredAtBlur: () => void
    handleOccurredUntilBlur: () => void
    handleOccurredUntilClear: () => void
    handleNoteBlur: () => void
    //
    formState: WellNotesFormT
    // states
    loading: boolean
    fetchError: ApolloError | undefined
    mutationError: ApolloError | undefined
    formType: "add" | "update"
    canManageInternalVisibility: boolean
}

const WellNotesFormComp: FunctionComponent<WellNotesComponentI> = ({
    handleNoteChange,
    handleOccurredAtChange,
    handleOccurredUntilChange,
    handleVisibilityChange,
    handleOccurredAtBlur,
    handleOccurredUntilBlur,
    handleOccurredUntilClear,
    handleNoteBlur,
    formState,
    formType,
    fetchError,
    mutationError,
    loading,
    canManageInternalVisibility,
}) => {
    const timezoneLabel = getDisplayStringFromZoneInfo(
        formState.data.zoneInfo ?? ZoneInfo.Unknown
    )
    const errExists = Boolean(fetchError) || Boolean(mutationError)
    const errMsg = fetchError
        ? "There was a problem getting the well note."
        : mutationError
        ? formType === "add"
            ? "There was a problem adding the well note."
            : "There was a problem updating the well note."
        : null

    return (
        <>
            <div
                className={classNames(
                    styles.formContainer,
                    loading && styles.loading
                )}
            >
                <form className={styles.form}>
                    <Input
                        type="textArea"
                        onChange={handleNoteChange}
                        onBlur={handleNoteBlur}
                        value={formState.data.note}
                        orientation="vertical"
                        inputCustomClass={styles.textArea}
                        message={
                            formState.validation.note
                                ? formState.validation.note
                                : ""
                        }
                        specializedClass={
                            formState.validation.note ? "danger" : "noMessage"
                        }
                        disabled={loading || errExists}
                    />
                    <div className={styles.fieldGroup}>
                        <div className={styles.timezoneHint}>
                            Times use your profile timezone: {timezoneLabel}
                        </div>
                        <Input
                            type="datetime-local"
                            onChange={handleOccurredAtChange}
                            onBlur={handleOccurredAtBlur}
                            value={formState.data.occurredAt}
                            orientation="vertical"
                            label="Occurred at"
                            disabled={loading || errExists}
                            message={
                                formState.validation.occurredAt
                                    ? formState.validation.occurredAt
                                    : ""
                            }
                            specializedClass={
                                formState.validation.occurredAt
                                    ? "danger"
                                    : "noMessage"
                            }
                        />
                        <Input
                            type="datetime-local"
                            onChange={handleOccurredUntilChange}
                            onBlur={handleOccurredUntilBlur}
                            value={formState.data.occurredUntil}
                            orientation="vertical"
                            label="Occurred until (optional)"
                            disabled={loading || errExists}
                            message={
                                formState.validation.occurredUntil
                                    ? formState.validation.occurredUntil
                                    : ""
                            }
                            specializedClass={
                                formState.validation.occurredUntil
                                    ? "danger"
                                    : "noMessage"
                            }
                        />
                        {formType === "update" &&
                            formState.data.occurredUntil && (
                                <div
                                    className={
                                        styles.clearOccurredUntilButtonContainer
                                    }
                                >
                                    <Button
                                        status="secondary"
                                        condition="default"
                                        handleButtonClick={
                                            handleOccurredUntilClear
                                        }
                                        disabled={loading || errExists}
                                    >
                                        Clear end time
                                    </Button>
                                </div>
                            )}
                    </div>
                    {canManageInternalVisibility && (
                        <div className={styles.visibilityGroup}>
                            <Input
                                type="select"
                                onChange={handleVisibilityChange}
                                value={formState.data.visibility}
                                orientation="vertical"
                                label="Visibility"
                                disabled={loading || errExists}
                                options={[
                                    {
                                        label: "Customer Visible",
                                        value: WellNoteVisibility.Shared,
                                    },
                                    {
                                        label: "Internal Only",
                                        value: WellNoteVisibility.Internal,
                                    },
                                ]}
                                specializedClass="noMessage"
                            />
                        </div>
                    )}
                </form>
            </div>
            {errExists && (
                <Alert message={errMsg} alertType="danger" isCloseable={true} />
            )}
        </>
    )
}

export default WellNotesFormComp
